import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { Header } from '@components/Header';
import { VsLogo } from '@components/Logo';
import { useAuthContext } from '@providers/AuthProvider';
import { useInitContext } from '@providers/InitProvider';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { useMutation, useQuery } from '@tanstack/react-query';
import { invitationsKeys } from 'app/queries/invitations/invitationsKeys';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router';
import { acceptInvitation, AcceptInvitationRejectedError } from './api/acceptInvitation';
import { createFetchInvitationQueryFn } from './api/fetchInvitation';
import { Content } from './components/Content';
import { NoUserContent } from './components/NoUserContent';
import { WrongAccountContent } from './components/WrongAccountContent';
import { canCurrentUserAcceptInvitation } from './helpers/canCurrentUserAcceptInvitation';
import { getAcceptInvitationErrorMessage } from './helpers/getAcceptInvitationErrorMessage';
import { getInvitationAuthPath } from './helpers/getInvitationAuthPath';
import { getInvitationExitPath } from './helpers/getInvitationExitPath';
import { getJoinedOrganizationSlug } from './helpers/getJoinedOrganizationSlug';
import { getOrganizationHomePath } from './helpers/getOrganizationHomePath';
import { Wrapper } from './styles/Wrapper';

export const AcceptInvitationPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{
    id: string;
  }>();
  const { call } = useAPI();
  const { isAuthenticated, logout } = useInitContext();
  const { me, organization, updateMe } = useAuthContext();
  const urlSearchParams = new URLSearchParams(location.search);
  const email = urlSearchParams.get('email');
  const invitationId = id ?? '';

  const invitationQuery = useQuery({
    queryKey: invitationsKeys.detail(invitationId),
    queryFn: createFetchInvitationQueryFn(call),
    enabled: Boolean(invitationId),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const invitation = invitationQuery.data?.status === 'CREATED' ? invitationQuery.data : undefined;
  const orgName = invitation?.organizationName ?? '';
  const isUnavailable = !invitationId || invitationQuery.isError || (invitationQuery.isSuccess && !invitation);
  const exitOrganizationSlug = organization?.slug ?? me?.organizations?.[0]?.slug;

  const acceptInvitationMutation = useMutation({
    mutationFn: () => acceptInvitation({ call, invitationId }),
    onSuccess: async () => {
      const previousOrganizationIds = (me?.organizations ?? []).map((org) => org.id);
      const updatedMe = await updateMe();
      const organizationSlug = getJoinedOrganizationSlug(
        previousOrganizationIds,
        updatedMe?.organizations ?? [],
        orgName,
      );

      navigate(getOrganizationHomePath(organizationSlug), { replace: true });
    },
    onError: (error) => {
      console.error('Unable to accept the invitation', error);
      toast.error(
        error instanceof AcceptInvitationRejectedError ? error.message : getAcceptInvitationErrorMessage(error),
      );
    },
  });

  useEffect(() => {
    if (!isUnavailable) {
      return;
    }

    if (isAuthenticated && !me?.id) {
      return;
    }

    navigate(
      getInvitationExitPath({
        isAuthenticated,
        organizationSlug: exitOrganizationSlug,
        email,
      }),
      { replace: true },
    );
  }, [isUnavailable, isAuthenticated, me?.id, exitOrganizationSlug, email, navigate]);

  if (
    invitationQuery.isLoading ||
    acceptInvitationMutation.isPending ||
    isUnavailable ||
    (isAuthenticated && !me?.id)
  ) {
    return (
      <Wrapper>
        <Helmet>
          <title>Request access - AutoScript</title>
        </Helmet>
        <LinearProgressLoader active />
      </Wrapper>
    );
  }

  if (!isAuthenticated || !invitation) {
    return (
      <Wrapper>
        <Helmet>
          <title>Request access - AutoScript</title>
        </Helmet>
        <NoUserContent id={invitationId} orgName={orgName} email={email} />
      </Wrapper>
    );
  }

  if (!canCurrentUserAcceptInvitation(invitation.userId, me.id)) {
    return (
      <Wrapper>
        <Helmet>
          <title>Request access - AutoScript</title>
        </Helmet>
        <Header title={<VsLogo />} />
        <WrongAccountContent
          orgName={orgName}
          invitedEmail={email}
          currentEmail={me.email ?? ''}
          onSwitchAccount={() =>
            void logout(getInvitationAuthPath({ authPath: routes.signin.make(), invitationId, email }))
          }
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Helmet>
        <title>Request access - AutoScript</title>
      </Helmet>
      <Header title={<VsLogo />} />
      <Content orgName={orgName} onAccept={() => acceptInvitationMutation.mutate()} />
    </Wrapper>
  );
};
